import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { SecaoProduto } from './SecaoProduto'
import { CurrencyField } from '@/components/common/CurrencyField'
import { politicaElegibilidadeService } from '@/services/produtoService'
import { mensagemErroElegibilidade } from '@/utils/errosElegibilidade'
import { mensagemErroPolitica } from '@/services/politicaProdutoService'
import {
  PARAMETROS_POR_TIPO,
  PRODUTOS_COM_TIPO_FIXO,
  ParametrosElegibilidade,
  PoliticaElegibilidade,
  PoliticasElegibilidadeProduto,
  ROTULO_TIPO_ELEGIBILIDADE,
  TAMANHO_MAXIMO_MOTIVO_ELEGIBILIDADE,
  TIPOS_ELEGIBILIDADE,
  TOOLTIP_AVALIACAO_ORIGINEI,
  TipoElegibilidade,
} from '@/types/elegibilidade'
import { fracaoComoPercentual, formatarDataIso } from '@/components/elegibilidade/formatoElegibilidade'
import { lerPercentualPtBr, percentualParaFracao } from '@/pages/simulacao/fracao'
import { formatCurrency, formatDateTime } from '@/utils/formatters'

const ROTULO_PARAMETRO: Record<keyof ParametrosElegibilidade, string> = {
  faturamentoAnualMinimo: 'Faturamento anual mínimo',
  valorOperacaoMinimo: 'Valor mínimo da operação',
  valorGrandesOperacoes: 'Grandes Operações a partir de',
  ltvMaximo: 'LTV máximo (crédito ÷ imóvel)',
}

const valorParametro = (chave: keyof ParametrosElegibilidade, v: number | null | undefined): string =>
  v == null ? '—' : chave === 'ltvMaximo' ? fracaoComoPercentual(v) : formatCurrency(v)

const vigencia = (p: PoliticaElegibilidade): string =>
  p.vigenciaFim
    ? `${formatarDataIso(p.vigenciaInicio)} a ${formatarDataIso(p.vigenciaFim)}`
    : `desde ${formatarDataIso(p.vigenciaInicio)}`

/** P6: só ATIVA conta como versão aberta/vigente (sem `status` = backend anterior à P6, era sempre ativa). */
const ehAtiva = (p: PoliticaElegibilidade): boolean => !p.status || p.status === 'ATIVA'

const ROTULO_SITUACAO: Record<string, string> = {
  PENDENTE_APROVACAO: 'Pendente de aprovação',
  ATIVA: 'Ativa',
  REJEITADA: 'Rejeitada',
  ENCERRADA: 'Encerrada',
}

/** Texto ao lado dos botões desabilitados para quem propôs (o backend recusa com 403 `APROVACAO_PELO_PROPONENTE`). */
export const TEXTO_PROPONENTE_ELEGIBILIDADE = 'Você propôs esta versão: aprovar ou rejeitar é de outra pessoa.'

const pessoa = (sub: string | null | undefined, eu: string | null | undefined): string =>
  !sub ? '—' : eu && sub === eu ? 'você' : sub

/** Hoje no fuso do navegador, ISO `YYYY-MM-DD`. */
const hojeIso = (): string => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Dia seguinte de uma data ISO `YYYY-MM-DD` (sem fuso: aritmética de calendário em UTC). */
const diaSeguinte = (iso: string): string => {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(a, m - 1, d + 1)).toISOString().slice(0, 10)
}

/**
 * Regra do servidor (VIGENCIA_CONFLITANTE): a nova versão começa DEPOIS do início da versão aberta (a anterior é
 * encerrada na véspera). Se a aberta começou hoje (ou começa no futuro), o primeiro dia possível é o seguinte ao
 * início dela — o campo vem pré-preenchido com ele. `null` = qualquer data a partir de hoje serve (vazio = hoje).
 */
const inicioMinimoNovaVersao = (aberta: PoliticaElegibilidade | null, hoje: string): string | null =>
  aberta && aberta.vigenciaInicio.slice(0, 10) >= hoje ? diaSeguinte(aberta.vigenciaInicio) : null

const parametrosTexto = (p: PoliticaElegibilidade): string =>
  PARAMETROS_POR_TIPO[p.tipo]?.length
    ? PARAMETROS_POR_TIPO[p.tipo].map((k) => `${ROTULO_PARAMETRO[k]}: ${valorParametro(k, p[k])}`).join(' · ')
    : 'Sem parâmetro — decisão da Originei'

interface Props {
  produtoId: string
  produtoCodigo?: string | null
  /** Só ADMIN cria versão (o servidor responde 403 aos demais). */
  podeEditar: boolean
  /** RETIRADA não recebe nova versão (422 `PRODUTO_RETIRADO`). */
  retirado?: boolean
  /** P6: GERENTE+ aprova/rejeita a proposta pendente (o servidor confere papel e pessoa distinta). */
  podeAprovar?: boolean
  /** `sub` do usuário logado — o proponente não aprova nem rejeita a própria proposta. */
  usuarioId?: string | null
}

/**
 * P2 (AN-PP2 §2/§8) — "Política de elegibilidade" do produto: tipo, parâmetros, versão,
 * vigência e origem da vigente + histórico (`GET /v1/produtos/{id}/politicas-elegibilidade`).
 * ADMIN: "Propor versão" com motivo obrigatório (`POST`, 202 PENDENTE_APROVACAO — nada muda até a
 * aprovação). P6 (AN-PP6 §3): GERENTE+ que não propôs aprova (a anterior é encerrada pelo servidor
 * na véspera do início da nova) ou rejeita com motivo. Erros 403/409/422 aparecem como vieram.
 * Fonte única da regra — oportunidades e simulação a recebem por evento (só na aprovação).
 */
export const PoliticaElegibilidadeProduto: FC<Props> = ({
  produtoId,
  produtoCodigo,
  podeEditar,
  retirado = false,
  podeAprovar = false,
  usuarioId = null,
}) => {
  const [resposta, setResposta] = useState<PoliticasElegibilidadeProduto | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [decidindo, setDecidindo] = useState(false)
  const [erroDecisao, setErroDecisao] = useState<string | null>(null)
  const [rejeitando, setRejeitando] = useState(false)
  const [motivoRejeicao, setMotivoRejeicao] = useState('')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setResposta(await politicaElegibilidadeService.listar(produtoId))
    } catch (e) {
      const status = (e as { status?: number } | null)?.status
      setErro(
        status === 403
          ? 'Sem permissão para ver a política de elegibilidade.'
          : status === 404
            ? 'Política de elegibilidade não encontrada para este produto.'
            : 'Não foi possível carregar a política de elegibilidade.'
      )
    } finally {
      setCarregando(false)
    }
  }, [produtoId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // Vigente HOJE vem do servidor (uma versão aberta com início futuro não é a vigente).
  const politicas = resposta?.historico ?? null
  const vigente = resposta?.vigente ?? null
  // A versão ABERTA (ATIVA sem fim) pode ser futura; é contra o início dela que o servidor confere a nova.
  // P6: proposta pendente/rejeitada também não tem fim, mas não é "aberta".
  const aberta =
    politicas?.find((p) => ehAtiva(p) && !p.vigenciaFim) ?? (vigente && !vigente.vigenciaFim ? vigente : null)
  const pendente = politicas?.find((p) => p.status === 'PENDENTE_APROVACAO') ?? null
  const tipoFixo = produtoCodigo ? PRODUTOS_COM_TIPO_FIXO[produtoCodigo] ?? null : null
  const propriaProposta = !!pendente && !!usuarioId && pendente.propostoPor === usuarioId

  const aprovar = async () => {
    if (!pendente) return
    setDecidindo(true)
    setErroDecisao(null)
    setAviso(null)
    try {
      await politicaElegibilidadeService.aprovar(produtoId, pendente.id, pendente.versaoRegistro)
      setAviso(`Versão v${pendente.versao} aprovada: vale a partir de ${formatarDataIso(pendente.vigenciaInicio)}.`)
      await carregar()
    } catch (e) {
      setErroDecisao(mensagemErroPolitica(e, 'Não foi possível aprovar a proposta.'))
    } finally {
      setDecidindo(false)
    }
  }

  const confirmarRejeicao = async () => {
    if (!pendente || !motivoRejeicao.trim()) return
    setDecidindo(true)
    setErroDecisao(null)
    setAviso(null)
    try {
      await politicaElegibilidadeService.rejeitar(produtoId, pendente.id, motivoRejeicao.trim(), pendente.versaoRegistro)
      setAviso(`Proposta v${pendente.versao} rejeitada; fica no histórico.`)
      setRejeitando(false)
      setMotivoRejeicao('')
      await carregar()
    } catch (e) {
      setErroDecisao(mensagemErroPolitica(e, 'Não foi possível rejeitar a proposta.'))
    } finally {
      setDecidindo(false)
    }
  }

  return (
    <SecaoProduto
      titulo="Política de elegibilidade"
      descricao="Regra versionada que o servidor aplica na reserva (alerta no cadastro; impede só o encaminhamento ao parceiro) e na simulação (alerta). Elegibilidade não equivale a aprovação de crédito."
      acao={
        podeEditar && !retirado ? (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAberto(true)} disabled={carregando}>
            Propor versão
          </Button>
        ) : undefined
      }
    >
      {aviso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      )}
      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} aria-label="Carregando política de elegibilidade" />
        </Box>
      ) : erro ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => void carregar()}>
              Tentar novamente
            </Button>
          }
        >
          {erro}
        </Alert>
      ) : !vigente ? (
        <Alert severity="warning" data-testid="sem-politica-vigente">
          Nenhuma política vigente: a avaliação fica "Política não sincronizada" — alerta no cadastro e impedimento
          no encaminhamento até o cadastro de uma versão.
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  Tipo
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {ROTULO_TIPO_ELEGIBILIDADE[vigente.tipo] ?? vigente.tipo}
                </Typography>
                {vigente.tipo === 'MANUAL_REVIEW' && (
                  <Typography variant="caption" color="text.secondary">
                    {TOOLTIP_AVALIACAO_ORIGINEI}
                  </Typography>
                )}
              </Paper>
            </Grid>
            {PARAMETROS_POR_TIPO[vigente.tipo].map((k) => (
              <Grid item xs={12} sm={4} key={k}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {ROTULO_PARAMETRO[k]}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {valorParametro(k, vigente[k])}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="text.secondary">
            Versão v{vigente.versao} · vigente {vigencia(vigente)}
            {vigente.origem ? ` · Origem: ${vigente.origem}` : ''}
            {vigente.legado ? ' · Legado — anterior à dupla aprovação' : ''}
          </Typography>
        </Stack>
      )}

      {!carregando && !erro && pendente && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }} data-testid="elegibilidade-pendente">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Proposta v{pendente.versao} aguardando aprovação
            </Typography>
            <Chip size="small" color="warning" variant="outlined" label="Pendente de aprovação" />
          </Stack>
          <Typography variant="body2">
            {ROTULO_TIPO_ELEGIBILIDADE[pendente.tipo] ?? pendente.tipo} · {parametrosTexto(pendente)}
          </Typography>
          <Typography variant="body2">A partir de {formatarDataIso(pendente.vigenciaInicio)}</Typography>
          <Typography variant="body2" color="text.secondary">
            Proposta por {pessoa(pendente.propostoPor ?? pendente.criadoPor, usuarioId)}
            {pendente.criadoEm ? ` em ${formatDateTime(pendente.criadoEm)}` : ''}
            {pendente.origem ? ` · Origem: ${pendente.origem}` : ''}
            {pendente.motivo ? ` — ${pendente.motivo}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Não vale até ser aprovada por outra pessoa (GERENTE ou ADMIN); a versão vigente continua valendo.
          </Typography>
          {erroDecisao && !rejeitando && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="erro-decisao-elegibilidade">
              {erroDecisao}
            </Alert>
          )}
          {podeAprovar && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1.5 }}>
              <Button variant="contained" size="small" disabled={propriaProposta || decidindo} onClick={() => void aprovar()}>
                Aprovar
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={propriaProposta || decidindo}
                onClick={() => {
                  setMotivoRejeicao('')
                  setErroDecisao(null)
                  setRejeitando(true)
                }}
              >
                Rejeitar
              </Button>
              {propriaProposta && (
                <Typography variant="body2" color="text.secondary">
                  {TEXTO_PROPONENTE_ELEGIBILIDADE}
                </Typography>
              )}
            </Stack>
          )}
        </Paper>
      )}

      {(politicas?.length ?? 0) > 1 && (
        <Box sx={{ mt: 2, overflowX: 'auto' }}>
          <Typography variant="overline">Histórico de versões</Typography>
          <Table size="small" aria-label="Histórico da política de elegibilidade">
            <TableHead>
              <TableRow>
                <TableCell>Versão</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Parâmetros</TableCell>
                <TableCell>Vigência</TableCell>
                <TableCell>Origem / motivo</TableCell>
                <TableCell>Aprovação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {politicas!.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    v{p.versao}{' '}
                    {(p.vigente === true || p.id === vigente?.id) && (
                      <Chip size="small" color="success" label="Vigente" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {p.legado && ehAtiva(p)
                      ? 'Legado — anterior à dupla aprovação'
                      : p.status
                        ? ROTULO_SITUACAO[p.status] ?? p.status
                        : '—'}
                  </TableCell>
                  <TableCell>{ROTULO_TIPO_ELEGIBILIDADE[p.tipo] ?? p.tipo}</TableCell>
                  <TableCell>{parametrosTexto(p)}</TableCell>
                  <TableCell>{vigencia(p)}</TableCell>
                  <TableCell>
                    {p.origem ?? '—'}
                    {p.motivo ? ` — ${p.motivo}` : ''}
                  </TableCell>
                  <TableCell>
                    {p.aprovadoPor
                      ? `Aprovada por ${pessoa(p.aprovadoPor, usuarioId)}${p.aprovadoEm ? ` em ${formatDateTime(p.aprovadoEm)}` : ''}`
                      : p.status === 'REJEITADA'
                        ? `Rejeitada${p.rejeitadoPor ? ` por ${pessoa(p.rejeitadoPor, usuarioId)}` : ''}${p.motivoRejeicao ? `: ${p.motivoRejeicao}` : ''}`
                        : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {aberto && (
        <NovaVersaoDialog
          produtoId={produtoId}
          vigente={vigente}
          aberta={aberta}
          tipoFixo={tipoFixo}
          onFechar={() => setAberto(false)}
          onCriada={(nova) => {
            setAberto(false)
            // 202 = proposta nova (PENDENTE); 200 = sem efeito: devolve a ATIVA igual ou a MESMA pendente.
            setAviso(
              nova.status === 'PENDENTE_APROVACAO'
                ? pendente && nova.id === pendente.id
                  ? `A regra informada é igual à proposta pendente (v${nova.versao}) — nada mudou.`
                  : `Versão v${nova.versao} proposta: fica pendente até a aprovação de outra pessoa (GERENTE ou ADMIN).`
                : (vigente && nova.id === vigente.id) || nova.status === 'ATIVA'
                  ? 'A regra informada é igual à vigente — nenhuma versão nova foi criada.'
                  : `Versão v${nova.versao} cadastrada.`
            )
            void carregar()
          }}
        />
      )}

      <Dialog open={rejeitando} onClose={() => (decidindo ? undefined : setRejeitando(false))} fullWidth maxWidth="sm">
        <DialogTitle>Rejeitar proposta {pendente ? `v${pendente.versao}` : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A proposta fica no histórico como rejeitada; a versão vigente continua valendo. O motivo é obrigatório.
          </Typography>
          {erroDecisao && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="erro-rejeicao-elegibilidade">
              {erroDecisao}
            </Alert>
          )}
          <TextField
            label="Motivo da rejeição"
            value={motivoRejeicao}
            onChange={(e) => setMotivoRejeicao(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: TAMANHO_MAXIMO_MOTIVO_ELEGIBILIDADE }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejeitando(false)} disabled={decidindo}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!motivoRejeicao.trim() || decidindo}
            onClick={() => void confirmarRejeicao()}
          >
            Confirmar rejeição
          </Button>
        </DialogActions>
      </Dialog>
    </SecaoProduto>
  )
}

const NovaVersaoDialog: FC<{
  produtoId: string
  vigente: PoliticaElegibilidade | null
  aberta: PoliticaElegibilidade | null
  tipoFixo: TipoElegibilidade | null
  onFechar: () => void
  onCriada: (p: PoliticaElegibilidade) => void
}> = ({ produtoId, vigente, aberta, tipoFixo, onFechar, onCriada }) => {
  const hoje = hojeIso()
  const inicioMinimo = inicioMinimoNovaVersao(aberta, hoje)
  const [tipo, setTipo] = useState<TipoElegibilidade>(tipoFixo ?? vigente?.tipo ?? 'MANUAL_REVIEW')
  const [faturamento, setFaturamento] = useState(vigente?.faturamentoAnualMinimo ?? 0)
  const [valorMin, setValorMin] = useState(vigente?.valorOperacaoMinimo ?? 0)
  const [valorGrandes, setValorGrandes] = useState(vigente?.valorGrandesOperacoes ?? 0)
  const [ltv, setLtv] = useState(
    vigente?.ltvMaximo != null ? fracaoComoPercentual(vigente.ltvMaximo).replace('%', '') : ''
  )
  const [vigenciaInicio, setVigenciaInicio] = useState(inicioMinimo ?? '')
  const [origem, setOrigem] = useState('')
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const parametros = PARAMETROS_POR_TIPO[tipo]
  const ltvPercentual = ltv.trim() ? lerPercentualPtBr(ltv) : null
  const ltvInvalido = parametros.includes('ltvMaximo') && (ltvPercentual == null || ltvPercentual <= 0 || ltvPercentual > 100)
  const completo =
    origem.trim().length > 0 &&
    motivo.trim().length > 0 &&
    (!parametros.includes('faturamentoAnualMinimo') || faturamento > 0) &&
    (!parametros.includes('valorOperacaoMinimo') || valorMin > 0) &&
    (!parametros.includes('valorGrandesOperacoes') || valorGrandes > 0) &&
    !ltvInvalido

  const salvar = async () => {
    setSalvando(true)
    setErro(null)
    try {
      // Só os parâmetros do tipo vão preenchidos (mesmo CHECK da V14); o servidor valida de novo.
      const nova = await politicaElegibilidadeService.criarVersao(produtoId, {
        tipo,
        faturamentoAnualMinimo: parametros.includes('faturamentoAnualMinimo') ? faturamento : null,
        valorOperacaoMinimo: parametros.includes('valorOperacaoMinimo') ? valorMin : null,
        valorGrandesOperacoes: parametros.includes('valorGrandesOperacoes') ? valorGrandes : null,
        ltvMaximo: parametros.includes('ltvMaximo') && ltvPercentual != null ? percentualParaFracao(ltvPercentual) : null,
        vigenciaInicio: vigenciaInicio || null,
        origem: origem.trim(),
        motivo: motivo.trim(),
      })
      onCriada(nova)
    } catch (e) {
      setErro(mensagemErroElegibilidade(e, 'Não foi possível cadastrar a nova versão.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open onClose={() => !salvando && onFechar()} maxWidth="sm" fullWidth>
      <DialogTitle>Propor versão da política de elegibilidade</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label="Tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoElegibilidade)}
            disabled={!!tipoFixo || salvando}
            helperText={tipoFixo ? 'Tipo fixo deste produto (Avaliação Originei).' : undefined}
            fullWidth
          >
            {TIPOS_ELEGIBILIDADE.map((t) => (
              <MenuItem key={t} value={t}>
                {ROTULO_TIPO_ELEGIBILIDADE[t]}
              </MenuItem>
            ))}
          </TextField>
          {parametros.includes('faturamentoAnualMinimo') && (
            <CurrencyField label="Faturamento anual mínimo (R$)" value={faturamento} onChange={setFaturamento} fullWidth required />
          )}
          {parametros.includes('valorOperacaoMinimo') && (
            <CurrencyField label="Valor mínimo da operação (R$)" value={valorMin} onChange={setValorMin} fullWidth required />
          )}
          {parametros.includes('valorGrandesOperacoes') && (
            <CurrencyField
              label="Grandes Operações a partir de (R$)"
              value={valorGrandes}
              onChange={setValorGrandes}
              fullWidth
              required
            />
          )}
          {parametros.includes('ltvMaximo') && (
            <TextField
              label="LTV máximo (%)"
              value={ltv}
              onChange={(e) => setLtv(e.target.value)}
              error={ltv.trim() !== '' && ltvInvalido}
              helperText="Percentual do valor do imóvel, ex.: 50 (inclusivo)."
              required
              fullWidth
            />
          )}
          {tipo === 'MANUAL_REVIEW' && <Alert severity="info">{TOOLTIP_AVALIACAO_ORIGINEI}</Alert>}
          {inicioMinimo && aberta && (
            <Alert severity="info" data-testid="vigencia-dia-seguinte">
              {aberta.vigenciaInicio.slice(0, 10) === hoje
                ? `A versão vigente (v${aberta.versao}) começou hoje. A nova versão vale a partir do dia seguinte (${formatarDataIso(inicioMinimo)}): a v${aberta.versao} é encerrada na véspera.`
                : `A versão v${aberta.versao} começa em ${formatarDataIso(aberta.vigenciaInicio)}. A nova versão só pode começar depois, a partir de ${formatarDataIso(inicioMinimo)}.`}
            </Alert>
          )}
          <TextField
            label="Início da vigência"
            type="date"
            value={vigenciaInicio}
            onChange={(e) => setVigenciaInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: inicioMinimo ?? hoje }}
            helperText={
              inicioMinimo
                ? `A partir de ${formatarDataIso(inicioMinimo)}. A versão anterior é encerrada na véspera.`
                : 'Vazio = hoje. Data no passado é recusada.'
            }
            fullWidth
          />
          <TextField
            label="Origem"
            value={origem}
            onChange={(e) => setOrigem(e.target.value)}
            required
            fullWidth
            inputProps={{ maxLength: 200 }}
            helperText="Documento ou decisão de onde vem a regra (ex.: PDF Políticas Vigentes v1.1)."
          />
          <TextField
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: TAMANHO_MAXIMO_MOTIVO_ELEGIBILIDADE }}
          />
          <Alert severity="info">
            A versão nasce pendente e só vale depois de aprovada por outra pessoa (GERENTE ou ADMIN).
          </Alert>
          {erro && <Alert severity="error">{erro}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void salvar()} disabled={!completo || salvando}>
          {salvando ? 'Enviando…' : 'Enviar para aprovação'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PoliticaElegibilidadeProduto
